// External libraries
import React, { useRef } from 'react';
import { useStoreState } from '@/store';

// Internal modules
import { appStore } from '@/store';
import { DisabledMask } from '../../DisabledMask';
import { CI, features, useMountLogging } from '@/lib';
import { Section } from '../StyledComponents';
import { formInputHandlers } from '../formInputHandlers';
// @ts-expect-error untyped import
import { payNowSubmitHandler } from '../handlers';
import {
  LazyBankFields,
  LazySplitCardFields,
  // @ts-expect-error untyped import
} from '../../PaymentInformation/LazyComponents';
import { CountryDropdown } from '../../Locale';
import { SectionHeader } from '../../Headings';
import { getPayNowHeaderContent, showBillingDetailsTitle } from './utils';
import { FlowTypes } from '../FlowTypes';
import {
  LazyAddress,
  LazyMemberBenefitsInterstitial,
  LazyMemberBenefitsTrueGuestsInterstitial,
  LazyShipping,
  // @ts-expect-error untyped import
} from '../Components';
import { LogBrowserAutoComplete } from '../LogBrowserAutoComplete';
import { useHandleFormFocus } from '../hooks/useHandleFormFocus';
import { ContactFieldsSection } from '../../ContactInformation';
import { LazyResidentialAddressInterstitial } from '../../Interstitials/ResidentialAddress/LazyResidentialAddress';
import {
  atomics,
  atomicsDomain,
  atomicsStatus,
  atomicsTasks,
  atomicsViewNames,
} from '@/lib/atomics';

export const PayNowForm = (): JSX.Element => {
  useMountLogging('PayNowForm');

  const onFocus = useHandleFormFocus();
  const { ui: uiActions } = appStore.getActions();

  // State
  const isShippingShown = useStoreState(({ ui }) => ui.isShippingShown);
  const fiType = useStoreState(({ ui }) => ui.fiType);

  // Features
  const withAddress = features.isNotActive('isAddressless');
  const showBankFields = fiType === 'bank' && features.isActive('hasAddBank');
  const isBoltActive = features.isActive('blockDomesticPayers');

  CI.endWith(
    showBankFields ? CI.Event.PAY_BANK_RENDER : CI.Event.PAY_NOW_RENDER,
    CI.Status.Ok,
  );

  const isMemberBenefitsInterstitialOpen = useStoreState(
    ({ ui }) => ui.isMemberBenefitsInterstitialOpen,
  );
  const isMemberBenefitsTrueGuestsInterstitialOpen = useStoreState(
    ({ ui }) => ui.isMemberBenefitsTrueGuestsInterstitialOpen,
  );
  const isResidentialAddressOpen = useStoreState(
    ({ ui }) => ui.isResidentialAddressOpen,
  );
  const isBlockedDomesticTransaction = useStoreState(
    ({ ui }) => ui.isBlockedDomesticTransaction,
  );

  // The shipping methods should be shown if either V1 or V2 shipping callbacks
  // are eligible
  const isOnShippingCallbackEligible = features.someActive(
    'isShippingCallbackV1Eligible',
    'isShippingCallbacksV2Eligible',
  );
  const hideShipping = features.isActive('hideShipping');

  const formRef = useRef<HTMLFormElement>(null);

  if (!hideShipping && isOnShippingCallbackEligible) {
    uiActions.setIsShippingShown(true);
  }

  return (
    <>
      <form
        noValidate
        onSubmit={payNowSubmitHandler}
        ref={formRef}
        onFocusCapture={onFocus}
      >
        <Section mb={'lg'}>
          <SectionHeader {...getPayNowHeaderContent(showBankFields)} />
          <CountryDropdown />
          <DisabledMask isDisabled={isBlockedDomesticTransaction}>
            <ContactFieldsSection
              fieldOrder={['email', 'phone', 'name']}
              mb="gutter"
              position="top"
              showHeading={false}
            />
          </DisabledMask>
          <LogBrowserAutoComplete />
        </Section>

        <DisabledMask isDisabled={isBoltActive || isBlockedDomesticTransaction}>
          <Section mb={'mb'}>
            {showBillingDetailsTitle()}
            {showBankFields ? <LazyBankFields /> : <LazySplitCardFields />}
          </Section>

          {withAddress && (
            <LazyAddress showCheckboxes={true} {...formInputHandlers} />
          )}
          {isShippingShown && <LazyShipping />}
          <FlowTypes />
        </DisabledMask>
      </form>
      {isMemberBenefitsInterstitialOpen && (
        <LazyMemberBenefitsInterstitial formRef={formRef} />
      )}
      {isMemberBenefitsTrueGuestsInterstitialOpen && (
        <LazyMemberBenefitsTrueGuestsInterstitial />
      )}
      {isResidentialAddressOpen && (
        <LazyResidentialAddressInterstitial
          submitHandler={payNowSubmitHandler}
        />
      )}
      <atomics.MarkUIReady
        viewName={atomicsViewNames.ryp}
        status={atomicsStatus.ok}
        domain={atomicsDomain}
        task={atomicsTasks.rypLoaded}
      />
    </>
  );
};
